import type { QueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/lib/auth-store';

/**
 * A TOKEN JOGKÉPÉNEK VÁLTOZÁSA → a származtatott szerver-állapot eldobása. EGY seam, MINDEN refresh-útra.
 *
 * ## Miért itt, és miért nem az `api.ts`-ben
 * A token HÁROM úton cserélődhet: (1) a `api.ts` 401→csendes refresh→retry ága, (2) az SSE
 * `permissions-changed` push (`use-notification-stream`), (3) a route-guard / oldal-újratöltés utáni
 * `tryRefresh`. Ha az invalidálást bármelyik ágba írnánk bele, a másik kettő kimaradna — és a kimaradás
 * NÉMA: a felület a régi jogkép szerint kapuzna tovább. A token-csere viszont MINDHÁROM úton a
 * `useAuthStore.setToken`-en megy át, ezért a helyes hely a store feliratkozása.
 *
 * ## Miért nem elég a szűk (`['session']`) invalidálás
 * A jog-változás nem csak a UI-kapuzást írja át, hanem az **adat-hozzáférést** is: az Odin RLS/scope
 * másképp szűr, így egy korábban letöltött lista MINDKÉT irányban elavulhat (kevesebb VAGY több sor
 * látszik). Ezért jog-változáskor a TELJES cache-t dobjuk.
 *
 * ## Miért nem invalidálunk MINDEN token-cserénél
 * A tokent 15 percenként a puszta lejárat is cseréli. Ha minden cserére teljes refetch menne, egy B2B
 * admin-felület negyedóránként újrahúzná az összes nyitott listát — fölösleges terhelés a felhasználón és
 * az Odinon. Ezért a kiváltó ok NEM a token-csere, hanem a JOGKÉP tényleges változása.
 *
 * ## A jogkép összehasonlítása — és ami ez NEM
 * A `scope` + `permissions` claimeket olvassuk ki a SAJÁT tokenünkből, és csak azt nézzük, hogy
 * **változtak-e**. Ez **NEM authorizáció**: itt semmilyen jog-döntés nem születik, aláírást nem
 * ellenőrzünk, és a claimeknek nem hiszünk el semmit. A jog-döntés forrás-igazsága változatlanul (a)
 * szerver-oldalon az Odin (`scope`-guard + RLS) és az Atlas BFF (`@RequirePermissions`), (b) a UI-kapuzásnál
 * a `/session` végpont válasza. Ez a modul kizárólag **cache-invalidálási jelzést** állít elő.
 * (A `pv` claimet SZÁNDÉKOSAN nem használjuk: az az Odin kibocsátó és a platform-guard közti protokoll —
 * a consumer-szerződés szerint a consumer ne értelmezze. A `scope`/`permissions` viszont dokumentáltan a
 * miénk, és pontosan azt írja le, ami a kapuzást meghatározza.)
 */

/**
 * A NEM ÉRTELMEZHETŐ token ujjlenyomata. SZÁNDÉKOSAN külön a „nincs token" (`null`) esettől: a kettő
 * összemosása elnyelné a helyreállást — egy csonka/hibás token UTÁN érkező ÉRVÉNYES token „bejelentkezésnek"
 * látszana, és a bejelentkezés-ág (jogosan) nem invalidál. Ez volt az első verzió hibája, teszt őrzi.
 */
const UNPARSEABLE = '\u0000unparseable';

/** A jogképet leíró claimek egyetlen, összehasonlítható ujjlenyomatként. `null` = nincs token (kijelentkezve). */
function permissionFingerprint(token: string | null): string | null {
  if (!token) return null;
  const payload = token.split('.')[1];
  if (!payload) return UNPARSEABLE;
  try {
    // base64url → base64 (a JWT base64url-t használ: `-`/`_`, és elhagyja a paddinget).
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const claims = JSON.parse(json) as { scope?: unknown; permissions?: unknown };
    const scope = typeof claims.scope === 'string' ? claims.scope.split(/\s+/).filter(Boolean) : [];
    const perms = Array.isArray(claims.permissions)
      ? claims.permissions.filter((p): p is string => typeof p === 'string')
      : [];
    // Rendezve: a claim-sorrend nem jelent jogkép-változást (a szerver nem garantál stabil sorrendet).
    return JSON.stringify([[...scope].sort(), [...perms].sort()]);
  } catch {
    // Értelmezhetetlen token → dedikált sentinel (NEM `null`!). FAIL-SAFE irány: a következő ÉRTELMES tokenre
    // eltérést látunk ÉS volt session, tehát inkább egy fölösleges invalidálás lesz, mint egy elmaradt.
    return UNPARSEABLE;
  }
}

/**
 * Feliratkozás a token-cserére: ha a jogkép ténylegesen változott, a teljes query-cache-t invalidáljuk.
 * Visszatérés: a leiratkozó függvény (teszthez / HMR-hez).
 */
export function watchPermissionChanges(queryClient: QueryClient): () => void {
  let previous = permissionFingerprint(useAuthStore.getState().token);
  return useAuthStore.subscribe((state) => {
    const next = permissionFingerprint(state.token);
    if (next === previous) return; // puszta lejárat-refresh (vagy kijelentkezés utáni null→null) → no-op
    const hadSession = previous !== null;
    previous = next;
    // BEJELENTKEZÉS (nincs token → van jogkép): NINCS mit eldobni, a cache üres — a felesleges invalidálás
    // csak dupla lekérést okozna a login utáni első renderen. Figyelem: ez KIZÁRÓLAG a `null`-ból induló
    // átmenet; a nem-értelmezhető tokenből (`UNPARSEABLE`) való helyreállás NEM ez, az invalidál.
    if (!hadSession) return;
    void queryClient.invalidateQueries();
  });
}
